import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { toast } from "sonner";
import { X, UserPlus, ShieldCheck, Pencil, Trash2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import EditUserDialog from "@/pages/users/EditUserDialog";

const ROLES = [
  { value: "admin", label: "Admin" },
  { value: "project_manager", label: "Project Manager" },
  { value: "compliance_officer", label: "Compliance Officer" },
  { value: "finance", label: "Finance" },
  { value: "dispatcher", label: "Dispatcher" },
  { value: "field_inspector", label: "Field Inspector" },
  { value: "driver", label: "Driver" },
  { value: "hauler", label: "Hauler" },
  { value: "receiver", label: "Receiver" },
  { value: "user", label: "User" },
];

const ROLE_STYLE = {
  admin: "bg-red-50 text-red-800 border-red-200",
  project_manager: "bg-orange-50 text-orange-800 border-orange-200",
  compliance_officer: "bg-indigo-50 text-indigo-800 border-indigo-200",
  finance: "bg-purple-50 text-purple-800 border-purple-200",
  dispatcher: "bg-blue-50 text-blue-800 border-blue-200",
  field_inspector: "bg-emerald-50 text-emerald-800 border-emerald-200",
  driver: "bg-amber-50 text-amber-800 border-amber-200",
  hauler: "bg-cyan-50 text-cyan-800 border-cyan-200",
  receiver: "bg-teal-50 text-teal-800 border-teal-200",
  user: "bg-gray-100 text-gray-800 border-gray-300",
};

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");

  const isAdmin = me?.role === "admin";

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
      setError("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const changeRole = async (id, role) => {
    try {
      await api.patch(`/users/${id}`, { role });
      toast.success(`Role updated to ${role}`);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const savePhone = async (id, phone) => {
    try {
      await api.patch(`/users/${id}`, { phone });
      toast.success("Phone updated");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`Deleted ${u.email}`);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (!isAdmin) {
    return (
      <div>
        <PageHeader title="Users" subtitle="Access control" />
        <div className="p-8">
          <div className="bg-white border border-gray-200 p-8 text-center">
            <ShieldCheck className="w-8 h-8 text-gray-400 mx-auto mb-3" />
            <div className="text-sm text-gray-600">Admin access required.</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Users"
        subtitle="Access control"
        actions={
          <button onClick={() => setInviting(true)} data-testid="new-user-button" className="bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold uppercase tracking-widest px-4 py-2.5 flex items-center gap-2">
            <UserPlus className="w-3.5 h-3.5" /> Invite User
          </button>
        }
      />
      <div className="p-8">
        {error && <div className="mb-4 px-3 py-2 bg-red-50 border border-red-200 text-red-800 text-sm">{error}</div>}
        <div className="bg-white border border-gray-200 overflow-x-auto" data-testid="users-table">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left px-4 py-2.5 label-caps">Name</th>
                <th className="text-left px-4 py-2.5 label-caps">Email</th>
                <th className="text-left px-4 py-2.5 label-caps">Phone</th>
                <th className="text-left px-4 py-2.5 label-caps">Role</th>
                <th className="text-left px-4 py-2.5 label-caps">Created</th>
                <th className="text-right px-4 py-2.5 label-caps">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr><td colSpan="6" className="px-4 py-12 text-center text-sm text-gray-500">No users.</td></tr>
              ) : users.map((u) => (
                <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`user-row-${u.email}`}>
                  <td className="px-4 py-3 text-sm text-gray-900">{u.name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-700">{u.email}</td>
                  <td className="px-4 py-3">
                    <PhoneCell user={u} onSave={savePhone} />
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={u.role}
                      onChange={(e) => changeRole(u.id, e.target.value)}
                      disabled={u.id === me?.id}
                      data-testid={`role-select-${u.email}`}
                      className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 border ${ROLE_STYLE[u.role] || "bg-gray-100"} cursor-pointer disabled:cursor-not-allowed`}
                    >
                      {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                    {u.id === me?.id && <span className="ml-2 text-[10px] uppercase text-gray-400">(you)</span>}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">{u.created_at?.slice(0, 10)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        onClick={() => setEditing(u)}
                        data-testid={`edit-user-${u.email}`}
                        title="Edit user / reset password"
                        className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => deleteUser(u)}
                        disabled={u.id === me?.id}
                        data-testid={`delete-user-${u.email}`}
                        title={u.id === me?.id ? "You cannot delete yourself" : "Delete user"}
                        className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-500 disabled:cursor-not-allowed"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {inviting && <InviteDialog onClose={() => setInviting(false)} onSaved={() => { setInviting(false); refresh(); }} />}
      {editing && (
        <EditUserDialog
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}

function InviteDialog({ onClose, onSaved }) {
  const [form, setForm] = useState({ email: "", name: "", phone: "", password: "", role: "user" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/register", form);
      toast.success("User invited");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white border border-gray-300 w-full max-w-md">
        <div className="border-b border-gray-200 px-5 py-4 flex items-center justify-between">
          <div className="font-display font-bold text-xl">Invite User</div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <F label="Name"><input required value={form.name} onChange={(e) => set("name", e.target.value)} className="inp" data-testid="invite-name-input" /></F>
          <F label="Email"><input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className="inp font-mono" data-testid="invite-email-input" /></F>
          <F label="Phone (for future SMS)">
            <input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="inp font-mono" data-testid="invite-phone-input" placeholder="+1 415-555-0000" />
          </F>
          <F label="Initial Password"><input type="password" required value={form.password} onChange={(e) => set("password", e.target.value)} className="inp font-mono" data-testid="invite-password-input" minLength={8} /></F>
          <F label="Role">
            <select value={form.role} onChange={(e) => set("role", e.target.value)} className="inp" data-testid="invite-role-select">
              {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </F>
          <div className="flex gap-3 pt-3">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-300 py-2.5 text-xs uppercase tracking-widest font-semibold">Cancel</button>
            <button type="submit" disabled={busy} data-testid="submit-invite-button" className="flex-1 bg-orange-500 hover:bg-orange-600 text-white py-2.5 text-xs uppercase tracking-widest font-semibold">Invite</button>
          </div>
        </form>
        <style>{`.inp { width: 100%; padding: 0.5rem 0.75rem; border: 1px solid #D1D5DB; font-size: 0.875rem; } .inp:focus { outline: none; border-color: #111827; }`}</style>
      </div>
    </div>
  );
}

function PhoneCell({ user, onSave }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(user.phone || "");
  useEffect(() => { setValue(user.phone || ""); }, [user.phone]);
  if (!editing) {
    return (
      <button
        type="button"
        className="text-xs font-mono text-gray-700 hover:text-gray-900 hover:underline text-left min-w-[8rem]"
        onClick={() => setEditing(true)}
        data-testid={`phone-cell-${user.email}`}
        title="Click to edit phone"
      >
        {user.phone || <span className="text-gray-300">— add —</span>}
      </button>
    );
  }
  const commit = () => {
    setEditing(false);
    if ((value || "").trim() !== (user.phone || "")) onSave(user.id, value.trim());
  };
  return (
    <input
      autoFocus
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setValue(user.phone || ""); setEditing(false); } }}
      className="text-xs font-mono px-2 py-1 border border-gray-900 w-40"
      data-testid={`phone-input-${user.email}`}
      placeholder="+1 415-555-0000"
    />
  );
}

function F({ label, children }) {
  return <div><label className="label-caps block mb-1.5">{label}</label>{children}</div>;
}
